import TableHeaderWithTab from "@/components/common/table/header-with-tab";
import { useAuthorization } from "@/hooks/use-authorization";
import {
  useActivateUser,
  useGetUsers,
  useInactivateUser,
  useResetUserPassword,
} from "@/queries/users/users.api";
import type { UserType } from "@/types/entities/users";
import { Permissions } from "@/utils/permissions";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  TableData,
  useToast,
} from "@elaniintc/goes-design-system";
import TablePaginantion from "@/components/common/table/pagination";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { useDebounceValue } from "usehooks-ts";
import DeactivateModal from "./details/edit/deactivate-modal";
import ReactivateModal from "./details/edit/reactivate-modal";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import {
  filterTypesEnum,
  searchTypesEnum,
  type FilterOption,
} from "@/types/common/filters";
import { useGetRolesAsInfinite } from "@/queries/roles";
import ResetPasswordModal from "./details/edit/reset-password-modal";
import type { AxiosError } from "axios";
import { TableLeadingCell } from "@/components/ui/table-leading-cell";
import { useGetApprovalGroupsByUser } from "@/queries/approval-groups";
import UserLeaderInGroupModal from "./user-leader-in-group-modal";
import type { ApprovalGroupType } from "@/types/entities/approval-groups";
import ExternalUsersList, { triggerExternalFiltersModal } from "./external";
import {
  useGetUserOrganizationsAsInfinite,
  useGetUserPositionsAsInfinite,
} from "@/queries/users/users.api";

const columnHelper = createColumnHelper<UserType>();

const columnsDef = ({
  onCellButtonClick,
  onDeactivate,
  onResetPassword,
  onActivate,
}: {
  onCellButtonClick: (id: string) => void;
  onDeactivate: (id: string) => void;
  onActivate: (id: string) => void;
  onResetPassword: (id: string) => void;
}) => {
  return [
    columnHelper.accessor("name", {
      id: "name",
      header: "Nombre",
      meta: {
        cellClassName: "group",
      },
      cell: (info) => {
        const { name, last_name, id } = info.row.original;
        return (
          <TableLeadingCell
            title={`${name || ""} ${last_name || ""}`.trim() || "-"}
            showButton
            onButtonClick={() => onCellButtonClick(id)}
          />
        );
      },
    }),
    columnHelper.accessor("user_roles", {
      id: "userRoles.name",
      header: "Rol Asignado",
      meta: {
        className: "hidden lg:table-cell",
      },
      cell: (info) => {
        if (!info.getValue() || info.getValue().length === 0) {
          return "-";
        }
        return info.getValue()[0]?.role_name || "-";
      },
    }),
    columnHelper.accessor("user_roles", {
      id: "userRoles.type",
      header: "Permisos",
      meta: {
        className: "hidden mdPlus:table-cell",
      },
      cell: (info) => {
        if (!info.getValue() || info.getValue().length === 0) {
          return "-";
        }
        const PermissionType = {
          editor: "Editor",
          reader: "Lector",
          admin: "Administrador",
        };
        const type =
          PermissionType[
            info.getValue()[0]?.role_type as keyof typeof PermissionType
          ] || "-";

        if (type === "-") {
          return type;
        }

        return (
          <Badge
            variant="neutral"
            shape="pill"
            className="h-6 text-sm leading-2xl"
          >
            {type}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("updated_at", {
      id: "updated_at",
      header: "Última Actualización",
      meta: {
        className: "hidden md:table-cell",
      },
      cell: (info) => {
        const date = info.getValue();
        return date ? dayjs(date).format("MMM DD, YYYY | HH:mm A") : "-";
      },
    }),
    columnHelper.accessor("user_status", {
      enableSorting: false,
      header: "Estado del Usuario",
      meta: {
        className: "hidden sm:table-cell",
      },
      cell: (info) => {
        const status = info.getValue();
        if (status === "Activo") {
          return (
            <Badge variant="success" shape="pill" size="sm">
              {status}
            </Badge>
          );
        }
        if (status === "Inactivo") {
          return (
            <Badge variant="neutral" shape="pill" size="sm">
              Inactivo
            </Badge>
          );
        }
        return "-";
      },
    }),
    columnHelper.accessor("createdBy", {
      id: "createdBy.name",
      header: "Creado por",
      meta: {
        className: "hidden xs:table-cell",
      },
      cell: (info) => {
        const createdBy = info.getValue();
        if (!createdBy) return "-";
        return (
          <div className="flex items-center gap-4">
            <Avatar size="xs">
              <AvatarFallback color="action">
                {createdBy.name?.charAt(0) || "-"}
              </AvatarFallback>
            </Avatar>
            <span className="flex-1 text-body text-base leading-3xl font-normal truncate">
              {`${createdBy.name || ""} ${createdBy.last_name || ""}`.trim()}
            </span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      meta: {
        className: "w-[56px]",
      },
      cell: (info) => {
        const userId = info.row.original.id;
        return (
          <div className="w-full flex justify-end">
            <Menu
              id={userId}
              isUserInactive={info.row.original.user_status === "Inactivo"}
              onActivate={() => onActivate(userId)}
              onDeactivate={() => onDeactivate(userId)}
              onResetPassword={() => onResetPassword(userId)}
            />
          </div>
        );
      },
    }),
  ];
};

const Menu = ({
  id,
  isUserInactive,
  onActivate,
  onDeactivate,
  onResetPassword,
}: {
  id: string;
  isUserInactive: boolean;
  onDeactivate: () => void;
  onActivate: () => void;
  onResetPassword: () => void;
}) => {
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions.users.update);
  const canUpdate = hasPermission(Permissions.users.update);
  const canResetPassword = hasPermission(Permissions.users["reset-password"]);

  const [state, setState] = useState<"idle" | "info">("idle");
  const { mutateAsync: getUserGroups } = useGetApprovalGroupsByUser(id);

  const [groups, setGroups] = useState<{ data: ApprovalGroupType[] } | null>(
    null,
  );

  const handleDeactivate = async () => {
    try {
      const userGroups = await getUserGroups();
      if (
        userGroups.data.some((g) =>
          g.members.some((m) => m.id === id && m.seniority === 1),
        )
      ) {
        setGroups(userGroups);
        setState("info");
      } else {
        setGroups(null);
        onDeactivate();
      }
    } catch {
      console.log("Error fetching user groups");
    }
  };

  return (
    <>
      {state === "info" && (
        <UserLeaderInGroupModal
          userId={id}
          groups={groups?.data || []}
          onClose={() => setState("idle")}
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="xs" className="size-8">
            <Icon
              type="far"
              name="ellipsis-vertical"
              className="text-current text-sm"
            />
            <span className="sr-only">Acciones para usuario</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[222px] min-w-56 rounded-lg border-neutral-silent border text-body font-normal p-2"
          side="bottom"
          align="end"
          sideOffset={4}
        >
          <DropdownMenuGroup>
            {canEdit && !isUserInactive && (
              <DropdownMenuItem
                className="hover:bg-neutral-object-accent-silent py-2 px-3"
                asChild
              >
                <NavLink to={`/usuarios/${id}`}>Editar usuario</NavLink>
              </DropdownMenuItem>
            )}
            {canUpdate && isUserInactive && (
              <DropdownMenuItem
                className="hover:bg-neutral-object-accent-silent py-2 px-3"
                onClick={onActivate}
              >
                Reactivar usuario
              </DropdownMenuItem>
            )}
            {canUpdate && !isUserInactive && (
              <DropdownMenuItem
                className="hover:bg-neutral-object-accent-silent py-2 px-3"
                onClick={handleDeactivate}
              >
                Desactivar usuario
              </DropdownMenuItem>
            )}
            {canResetPassword && (
              <DropdownMenuItem
                className="hover:bg-neutral-object-accent-silent py-2 px-3"
                onClick={onResetPassword}
              >
                Restablecer contraseña
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

const UsersPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuthorization();
  const canCreate = hasPermission(Permissions.users.create);
  const canEdit = hasPermission(Permissions.users.update);
  const canUpdate = hasPermission(Permissions.users.update);
  const canResetPassword = hasPermission(Permissions.users["reset-password"]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [activeTab, setActiveTab] = useQueryState(
    "user-tab",
    parseAsString.withDefault("internos"),
  );

  useEffect(() => {
    sessionStorage.setItem("users-active-tab", activeTab);
  }, [activeTab]);

  const [page, setPage] = useState<number>(1);

  const [query, setQuery] = useQueryState("search", parseAsString);
  const [queryExternal, setQueryExternal] = useQueryState(
    "search_external",
    parseAsString,
  );
  const [created_at, setCreatedAt] = useQueryState("created_at", parseAsString);
  const [status, setStatus] = useQueryState(
    "status",
    parseAsString.withDefault("Activo"),
  );

  const [order_by, setOrderBy] = useQueryState(
    "order_by",
    parseAsString.withDefault("!created_at"),
  );
  const [role_id, setRoleId] = useQueryState(
    "role_id",
    parseAsString.withDefault(""),
  );
  const [position_id, setPositionId] = useQueryState(
    "position_id",
    parseAsString.withDefault(""),
  );
  const [institution_id, setInstitutionId] = useQueryState(
    "institution_id",
    parseAsString.withDefault(""),
  );
  const [searchInput, setSearchInput] = useState<string | null>(query || "");
  const [searchInputExternal, setSearchInputExternal] = useState<string | null>(
    queryExternal || "",
  );
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);
  const [deferredQueryExternal] = useDebounceValue(
    searchInputExternal || "",
    500,
  );

  const [formatFilters, setFormatFilters] = useState<FilterOption[]>([
    {
      id: 1,
      field: "created_at",
      type: filterTypesEnum.dates,
      label: "Fecha de creación",
      isUpdated: !!created_at,
      shouldDisabledFutureDates: true,
      result: created_at || "",
    },
    {
      id: 2,
      field: "status",
      type: filterTypesEnum.status,
      label: "Estado del usuario",
      isUpdated: !!status,
      result: status || "Activo",
      status: [
        {
          field: "Activo",
          label: "Activo",
          value: status.includes("Activo"),
        },
        {
          field: "Inactivo",
          label: "Inactivo",
          value: status.includes("Inactivo"),
        },
        {
          field: "Bloqueado",
          label: "Bloqueado",
          value: status.includes("Bloqueado"),
        },
      ],
    },
    {
      id: 3,
      field: "roles",
      type: filterTypesEnum.textsearch,
      label: "Roles",
      searchField: "search",
      isUpdated: !!role_id,
      result: role_id || "",
      searchType: searchTypesEnum.roles,
      selected: [],
      query: useGetRolesAsInfinite,
    },
    {
      id: 4,
      field: "position",
      type: filterTypesEnum.textsearch,
      label: "Cargo",
      searchField: "search",
      isUpdated: !!position_id,
      result: position_id || "",
      searchType: searchTypesEnum.positions,
      selected: [],
      query: useGetUserPositionsAsInfinite,
    },
    {
      id: 5,
      field: "institution",
      type: filterTypesEnum.textsearch,
      label: "Institución",
      searchField: "search",
      isUpdated: !!institution_id,
      result: institution_id || "",
      searchType: searchTypesEnum.institutions,
      selected: [],
      query: useGetUserOrganizationsAsInfinite,
    },
  ]);

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  useEffect(() => {
    setQueryExternal(deferredQueryExternal || null);
  }, [deferredQueryExternal, setQueryExternal]);

  const { data, isFetching } = useGetUsers({
    page,
    per_page: 20,
    status: (status || "Activo") as "Inactivo" | "Activo" | "Bloqueado",
    ...(order_by
      ? {
          group_by: order_by,
        }
      : {}),
    ...(deferredQuery ? { search: deferredQuery } : {}),
    ...(created_at ? { created_at } : {}),
    ...(role_id ? { role: role_id } : {}),
    ...(position_id ? { position: position_id } : {}),
    ...(institution_id ? { institution: institution_id } : {}),
    relationships: ["userRoles", "createdBy"],
  });

  const shouldShowSkeleton = !data;

  //const [userToActivateOrInactivate, setUserToActivateOrInactivate] = useState<string | null>(null);
  const [selectedUserToAction, setSelectedUserToAction] = useState<
    string | null
  >(null);
  const [modalState, setModalState] = useState<
    | "idle"
    | "deactivate-warning"
    | "reactivate-warning"
    | "reset-password"
    | "filters"
  >("idle");

  const {
    mutateAsync: mutateResetPassword,
    isPending: isLoadingResetPassword,
  } = useResetUserPassword(selectedUserToAction!);

  const onResetPassword = async () => {
    try {
      await mutateResetPassword();
      showToast({
        title: "Cambio de contraseña solicitado",
        description:
          "El usuario recibirá una solicitud para completar el proceso de restablecimiento de contraseña.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square">
                <Icon type="fas" name="key" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (e) {
      const error = e as AxiosError<{ message: string }>;
      console.log("Error resetting password:", error);
      showToast({
        title: "Error al intentar restablecer contraseña",
        description:
          "La solicitud de restablecimiento de contraseña no pudo ser enviado, inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
    setSelectedUserToAction(null);
    setModalState("idle");
  };

  const {
    mutateAsync: mutateToInactiveUser,
    isPending: isLoadingInactivateUser,
  } = useInactivateUser(selectedUserToAction!);
  const { mutateAsync: mutateToActiveUser, isPending: isLoadingActivateUser } =
    useActivateUser(selectedUserToAction!);

  const columns = useMemo(() => {
    const c = columnsDef({
      onDeactivate: (id: string) => {
        setSelectedUserToAction(id);
        setModalState("deactivate-warning");
      },
      onResetPassword: (id: string) => {
        setSelectedUserToAction(id);
        setModalState("reset-password");
      },
      onActivate: (id: string) => {
        setSelectedUserToAction(id);
        setModalState("reactivate-warning");
      },
      onCellButtonClick: (id: string) => {
        navigate(`/usuarios/${id}`);
      },
    });
    return c.filter(({ id }) => {
      if (id === "actions") {
        return canEdit || canUpdate || canResetPassword;
      }
      return true;
    });
  }, [canEdit, canUpdate, canResetPassword, navigate]);

  const onDeactivate = async (descrition: string) => {
    try {
      await mutateToInactiveUser({ description: descrition });
      setSelectedUserToAction(null);
      setModalState("idle");
      showToast({
        title: "Usuario desactivado",
        description: "El usuario ha sido desactivado exitosamente.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="danger" shape="square">
                <Icon type="fas" name="user-minus" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (error) {
      setSelectedUserToAction(null);
      showToast({
        title: "Error al desactivar usuario",
        description: "No se pudo desactivar el usuario. Inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
      console.error("Error deactivating user:", error);
    }
  };

  const onActivate = async (descrition: string) => {
    try {
      await mutateToActiveUser({ description: descrition });
      setSelectedUserToAction(null);
      setModalState("idle");
      showToast({
        title: "Usuario reactivado",
        description: "El usuario ha sido reactivado exitosamente.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="success" shape="square">
                <Icon type="fas" name="user-check" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (error) {
      showToast({
        title: "Error al reactivar usuario",
        description: "No se pudo reactivar el usuario. Inténtalo de nuevo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
      setSelectedUserToAction(null);
      console.error("Error activating user:", error);
    }
  };

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "created_at") {
          setCreatedAt(f.result || null);
        }
        if (f.field === "status") {
          setStatus(f.result || null);
        }
        if (f.field === "roles") {
          setRoleId(f.result || null);
        }
        if (f.field === "position") {
          setPositionId(f.result || null);
        }
        if (f.field === "institution") {
          setInstitutionId(f.result || null);
        }
        setPage(1);
      }
    });

    //set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };
  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters([
      {
        id: 1,
        field: "created_at",
        type: filterTypesEnum.dates,
        label: "Fecha de creación",
        shouldDisabledFutureDates: true,
        isUpdated: false,
        result: "",
      },
      {
        id: 2,
        field: "status",
        type: filterTypesEnum.status,
        label: "Estado del usuario",
        isUpdated: true,
        result: "Activo",
        status: [
          {
            field: "Activo",
            label: "Activo",
            value: true,
          },
          {
            field: "Inactivo",
            label: "Inactivo",
            value: false,
          },
          {
            field: "Bloqueado",
            label: "Bloqueado",
            value: false,
          },
        ],
      },
      {
        id: 3,
        field: "roles",
        type: filterTypesEnum.textsearch,
        label: "Roles",
        searchField: "search",
        isUpdated: false,
        result: "",
        searchType: searchTypesEnum.roles,
        selected: undefined,
        query: useGetRolesAsInfinite,
      },
      {
        id: 4,
        field: "position",
        type: filterTypesEnum.textsearch,
        label: "Cargo",
        searchField: "search",
        isUpdated: false,
        result: "",
        searchType: searchTypesEnum.positions,
        selected: undefined,
        query: useGetUserPositionsAsInfinite,
      },
      {
        id: 5,
        field: "institution",
        type: filterTypesEnum.textsearch,
        label: "Institución",
        searchField: "search",
        isUpdated: false,
        result: "",
        searchType: searchTypesEnum.institutions,
        selected: undefined,
        query: useGetUserOrganizationsAsInfinite,
      },
    ]);
    setCreatedAt(null);
    setRoleId(null);
    setPositionId(null);
    setInstitutionId(null);
    setStatus("Activo");
    setModalState("idle");
  };

  console.log("formatFilters", formatFilters);

  return (
    <>
      {modalState === "reset-password" && selectedUserToAction && (
        <ResetPasswordModal
          isOpen={modalState === "reset-password"}
          isLoading={isLoadingResetPassword}
          onClose={() => {
            setSelectedUserToAction(null);
            setModalState("idle");
          }}
          onAccept={onResetPassword}
        />
      )}
      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            const filters = onApplyFilters(newFilters);
            setFormatFilters(filters);
            setModalState("idle");
          }}
        />
      )}
      {modalState === "deactivate-warning" && selectedUserToAction && (
        <DeactivateModal
          isOpen={modalState === "deactivate-warning"}
          onClose={() => {
            setSelectedUserToAction(null);
            setModalState("idle");
          }}
          userId={selectedUserToAction || null}
          isLoading={isLoadingInactivateUser}
          onAccept={({ description }) => {
            if (selectedUserToAction) {
              onDeactivate(description);
            }
          }}
        />
      )}

      {modalState === "reactivate-warning" && selectedUserToAction && (
        <ReactivateModal
          isOpen={modalState === "reactivate-warning"}
          onClose={() => {
            setSelectedUserToAction(null);
            setModalState("idle");
          }}
          userId={selectedUserToAction || null}
          isLoading={isLoadingActivateUser}
          onAccept={({ description }) => {
            if (selectedUserToAction) {
              onActivate(description);
            }
          }}
        />
      )}

      <div className="h-full flex flex-col gap-3">
        <p className="font-normal text-body leading-3xl">
          Administra los usuarios con acceso a la plataforma SDT.
        </p>
        <div className="h-full min-h-[500px] relative">
          <div className="h-full flex flex-col bg-neutral-object border-neutral border-[0.5px] rounded-xl shadow-neutral-md overflow-hidden">
            <TableHeaderWithTab
              tabs={[
                {
                  label: "Internos",
                  value: "internos",
                },
                {
                  label: "Externos",
                  value: "externos",
                },
              ]}
              activeTab={activeTab}
              onTabChange={(tab) => {
                setActiveTab(tab as "internos" | "externos");
              }}
              placeholder={
                activeTab === "internos"
                  ? "Buscar usuario..."
                  : "Buscar usuario externo..."
              }
              search={
                activeTab === "internos" ? searchInput : searchInputExternal
              }
              isLoading={isFetching}
              setSearch={(value) => {
                if (activeTab === "internos") {
                  setSearchInput(value);
                } else {
                  setSearchInputExternal(value);
                }
              }}
              canCreate={canCreate}
              createLabel={
                activeTab === "internos"
                  ? "Crear usuario"
                  : "Crear usuario externo"
              }
              onCreate={() =>
                navigate(
                  activeTab === "internos"
                    ? "/usuarios/new"
                    : "/usuarios/externos/new",
                )
              }
              onFilter={() => {
                if (activeTab === "externos") {
                  triggerExternalFiltersModal();
                } else {
                  setModalState("filters");
                }
              }}
              filtersApplied={
                activeTab === "internos"
                  ? formatFilters.filter((f) => f.isUpdated).length
                  : 0
              }
            />
            {activeTab === "internos" ? (
              <>
                <TableData
                  wrapperClassName="min-h-0 flex-1 overflow-auto h-full relative"
                  headerClassName="sticky top-0 bg-neutral-object z-20"
                  columns={columns as ColumnDef<UserType>[]}
                  data={data?.data || []}
                  sorting={sorting}
                  onSortingChange={(val) => {
                    setSorting(val);
                    if (val.length === 0) {
                      setOrderBy("!created_at");
                      return;
                    }
                    setOrderBy(
                      val.length > 0
                        ? `${val[0]?.desc ? "!" : ""}${val[0].id}`
                        : "!created_at",
                    );
                  }}
                  isFirstLoading={shouldShowSkeleton}
                  stripped
                />
                <div className="p-3">
                  <TablePaginantion
                    totalPages={data?.meta?.last_page || 0}
                    totalItems={data?.meta?.total || 0}
                    pageSize={data?.data.length || 0}
                    currentPage={data?.meta?.current_page || 1}
                    hasNext={
                      (data?.meta?.current_page || 0) <
                      (data?.meta?.last_page || 0)
                    }
                    hasPrevious={(data?.meta?.current_page || 0) > 1}
                    onPageChange={(page) => {
                      setPage(page);
                    }}
                    onNext={() => {
                      if (data?.meta?.current_page && data?.meta?.last_page) {
                        if (data.meta?.current_page < data.meta?.last_page) {
                          setPage(data.meta?.current_page + 1);
                        }
                      }
                    }}
                    onPrevious={() => {
                      if (data?.meta?.current_page && data?.meta?.last_page) {
                        if (data.meta?.current_page > 1) {
                          setPage(data.meta?.current_page - 1);
                        }
                      }
                    }}
                  />
                </div>
              </>
            ) : (
              <ExternalUsersList searchQuery={deferredQueryExternal} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UsersPage;
